import { useTranslation } from 'react-i18next';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { TenantAssistantConfigurationForm } from '@/components/assistantConfiguration/TenantAssistantConfigurationForm';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { MODULE_KEY } from '@/constants/modules';
import { useTenantAssistantConfigurationQuery } from '@/hooks/useAssistantConfiguration';
import { useHasPlatformPermission } from '@/hooks/useHasPlatformPermission';
import { useTenantModulesQuery } from '@/hooks/useTenantModulesQuery';
import { AssistantUsageStrip } from './AssistantUsageStrip';

interface AssistantTabProps {
  tenantId: string;
  tenantName: string;
}

export function AssistantTab({ tenantId, tenantName }: AssistantTabProps) {
  const canSeeUsage = useHasPlatformPermission('assistant_usage.read');
  const { t } = useTranslation('assistant');
  const configurationQuery = useTenantAssistantConfigurationQuery(tenantId);
  const modulesQuery = useTenantModulesQuery(tenantId);
  const assistantModuleState =
    modulesQuery.data === undefined
      ? undefined
      : modulesQuery.data.find(function isAssistantModule(state) {
          return state.moduleKey === MODULE_KEY.aiAssistant;
        });
  const isModuleOff =
    assistantModuleState !== undefined && assistantModuleState.isEnabled === false;

  if (configurationQuery.isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  if (configurationQuery.isError === true || configurationQuery.data === undefined) {
    return (
      <QueryErrorState
        onRetry={function retry() {
          configurationQuery.refetch();
        }}
      />
    );
  }

  return (
    <div>
      <h2 className="text-sm font-bold text-zooindigo">{t('configuration.tenantTitle')}</h2>
      <p className="text-xs text-zoomuted mt-1 mb-5">{t('configuration.tenantSubtitle')}</p>
      {canSeeUsage ? <AssistantUsageStrip tenantId={tenantId} tenantName={tenantName} /> : null}
      {isModuleOff === true ? (
        <p className="mb-5 flex items-center gap-2 px-3.5 py-2.5 bg-zoowarningbg border border-zoowarningtext rounded-xl text-xs font-medium text-zoowarningtext">
          <Icon name="AlertTriangle" className="shrink-0" />
          {t('configuration.moduleOffNotice')}
        </p>
      ) : null}
      <TenantAssistantConfigurationForm
        key={tenantId}
        tenantId={tenantId}
        configuration={configurationQuery.data}
      />
    </div>
  );
}
